import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Container, Typography, Box } from '@mui/material';
import { ReactComponent as Blazon } from '../images/blazon.svg';
import { FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import '../css/HomeStyles.css';
import '../css/LvivStyles.css';

function Lviv() {
    const [form, setForm] = useState({
        resolutionSeries: 'ЛВ',
        resolutionNumber: '',
        fullName: '',
        email: '',
        carNumber: '',
        payerInn: '',
        amount: ''
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [serviceFee, setServiceFee] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [paymentMessage, setPaymentMessage] = useState(null);

    const location = useLocation();

    useEffect(() => {
        // SEO / meta tags
        document.title = 'Оплата штрафу за паркування у м. Львів | PayFine';

        const description = 'Онлайн-оплата штрафів за паркування у місті Львів: зручна форма, автоматичне формування призначення платежу та офіційні бюджетні реквізити.';
        let metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.name = 'description';
            document.head.appendChild(metaDesc);
        }
        metaDesc.content = description;

        const canonicalHref = `${window.location.origin}/lviv`;
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
        }
        canonical.href = canonicalHref;
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const fromPayment = params.get('fromPayment');
        const status = params.get('status');

        // Prefill from query params (e.g. /lviv?violation_number=ЛВ9201052357&car_number=HC0001AA&amount=680)
        // We only fill fields that are currently empty to avoid overriding user input.
        const violationNumberRaw = params.get('violation_number');
        const carNumberRaw = params.get('car_number');
        const amountRaw = params.get('amount');

        if (violationNumberRaw || carNumberRaw || amountRaw) {
            const v = (violationNumberRaw || '').trim();
            const series = v.startsWith('РАП') ? 'РАП' : 'ЛВ';
            const numberDigits = v.replace(/\D/g, '');

            const parsedAmount = (() => {
                if (!amountRaw) return null;
                const normalized = String(amountRaw).replace(',', '.').trim();
                const num = parseFloat(normalized);
                return Number.isFinite(num) ? num : null;
            })();

            setForm((prev) => {
                const next = { ...prev };
                if (violationNumberRaw) {
                    if (!prev.resolutionSeries) next.resolutionSeries = series;
                    if (!prev.resolutionNumber) next.resolutionNumber = numberDigits;
                    if (prev.resolutionSeries === prev.resolutionSeries && prev.resolutionSeries !== series && !prev.resolutionNumber) {
                        next.resolutionSeries = series;
                    }
                }
                if (carNumberRaw && !prev.carNumber) next.carNumber = String(carNumberRaw).trim();
                if (parsedAmount != null && !prev.amount) next.amount = String(parsedAmount);
                return next;
            });

            if (parsedAmount != null) {
                const fee = Math.ceil(parsedAmount * 0.15);
                setServiceFee(fee);
                setTotalAmount(parsedAmount + fee);
            }
        }

        if (fromPayment === 'true') {
            if (status === 'success') {
                setPaymentMessage({
                    type: 'success',
                    text: 'Оплата успішно виконана. Квитанцію від LiqPay ви отримаєте на вказану електронну пошту.'
                });
            } else if (status === 'failure' || status === 'error') {
                setPaymentMessage({
                    type: 'error',
                    text: 'Оплату не було завершено. Спробуйте, будь ласка, ще раз або перевірте дані картки.'
                });
            } else {
                setPaymentMessage({
                    type: 'info',
                    text: 'Повернення зі сторінки оплати. Якщо LiqPay показав успішну транзакцію, кошти будуть зараховані на рахунок міста протягом кількох банківських днів.'
                });
            }
        }
    }, [location.search]);

    const buildPaymentPurpose = () => {
        const series = form.resolutionSeries || 'ЛВ';
        const number = form.resolutionNumber || '';
        const name = form.fullName || '';
        const car = form.carNumber || '';
        const inn = form.payerInn || '';
        return `21081100;${series};${number};${name};${car};${inn}`;
    };

    const formatUah = (value) => {
        const n = Number.isFinite(value) ? value : 0;
        return n.toFixed(2).replace(',', '.');
    };

    const handleChange = (field) => (event) => {
        const rawValue = event.target.value;
        let value = rawValue;

        if (field === 'resolutionNumber') {
            value = rawValue.replace(/\D/g, '');
        }

        setForm((prev) => ({
            ...prev,
            [field]: value
        }));

        if (field === 'amount') {
            const numeric = parseFloat(value.replace(',', '.')) || 0;
            const fee = Math.ceil(numeric * 0.15);
            setServiceFee(fee);
            setTotalAmount(numeric + fee);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');

        if (
            !form.resolutionSeries ||
            !form.resolutionNumber ||
            !form.fullName ||
            !form.email ||
            !form.carNumber ||
            !form.amount
        ) {
            setError('Будь ласка, заповніть усі обовʼязкові поля.');
            return;
        }

        try {
            setLoading(true);

            const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;

            const resolutionFull = `${form.resolutionSeries}${form.resolutionNumber}`;
            const msgNumber = String(form.resolutionNumber).replace(/\D/g, '') || form.resolutionNumber;

            const baseAmount = parseFloat(form.amount);
            const fee = Math.ceil(baseAmount * 0.15);
            const liqpayAmount = baseAmount + fee;
            const paymentPurpose = buildPaymentPurpose();

            const paymentData = {
                amount: liqpayAmount,
                currency: 'UAH',
                description: paymentPurpose,
                orderId: `order_${msgNumber}`,
                carNumber: form.carNumber,
                fullName: form.fullName,
                email: form.email,
                penaltyAmount: baseAmount
            };

            const response = await axios.post(`${baseUrl}/api/createLiqPayPayment`, paymentData);

            const { data, signature } = response.data;

            const formElement = document.createElement('form');
            formElement.setAttribute('method', 'POST');
            formElement.setAttribute('action', 'https://www.liqpay.ua/api/3/checkout');
            formElement.setAttribute('accept-charset', 'utf-8');

            const dataInput = document.createElement('input');
            dataInput.setAttribute('type', 'hidden');
            dataInput.setAttribute('name', 'data');
            dataInput.setAttribute('value', data);
            formElement.appendChild(dataInput);

            const signatureInput = document.createElement('input');
            signatureInput.setAttribute('type', 'hidden');
            signatureInput.setAttribute('name', 'signature');
            signatureInput.setAttribute('value', signature);
            formElement.appendChild(signatureInput);

            document.body.appendChild(formElement);
            formElement.submit();
            document.body.removeChild(formElement);
        } catch (e) {
            console.error('Error creating LiqPay payment for Lviv:', e);
            setError('Не вдалося створити оплату. Спробуйте, будь ласка, ще раз.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container
            maxWidth="false"
            className="container"
            style={{ minHeight: '100vh', justifyContent: 'space-between' }}
        >
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', paddingBottom: 32 }}>
                <div className="form-container">
                    <div className="icon">
                        <Blazon width={80} height={80} />
                    </div>

                    <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: '#333', fontFamily: 'e-UkraineHead', textAlign: 'center' }}>
                        Оплата штрафу за паркування у м. Львів
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 3, color: '#555', fontFamily: 'e-Ukraine', textAlign: 'center' }}>
                        Заповніть поля нижче, щоб сплатити штраф онлайн. Кошти зараховуються на рахунок міста протягом трьох робочих днів.
                    </Typography>

                    {paymentMessage && (
                        <div
                            style={{
                                marginBottom: 16,
                                padding: 10,
                                borderRadius: 8,
                                border: '1px solid',
                                borderColor:
                                    paymentMessage.type === 'success'
                                        ? 'rgba(56, 142, 60, 0.4)'
                                        : paymentMessage.type === 'error'
                                        ? 'rgba(211, 47, 47, 0.4)'
                                        : 'rgba(25, 118, 210, 0.4)',
                                backgroundColor:
                                    paymentMessage.type === 'success'
                                        ? 'rgba(56, 142, 60, 0.06)'
                                        : paymentMessage.type === 'error'
                                        ? 'rgba(211, 47, 47, 0.06)'
                                        : 'rgba(25, 118, 210, 0.04)',
                                color:
                                    paymentMessage.type === 'success'
                                        ? '#2e7d32'
                                        : paymentMessage.type === 'error'
                                        ? '#c62828'
                                        : '#1565c0',
                                fontSize: 13
                            }}
                        >
                            {paymentMessage.text}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="lviv-fields">
                            <div className="input-field">
                                <label htmlFor="resolutionNumber">Серія та номер повідомлення / постанови</label>
                                <div className="lviv-resolution-group">
                                    <select
                                        id="resolutionSeries"
                                        value={form.resolutionSeries}
                                        onChange={handleChange('resolutionSeries')}
                                        className="lviv-resolution-prefix-select"
                                        aria-label="Серія"
                                    >
                                        <option value="ЛВ">ЛВ</option>
                                        <option value="РАП">РАП</option>
                                    </select>
                                    <input
                                        type="text"
                                        id="resolutionNumber"
                                        value={form.resolutionNumber}
                                        onChange={handleChange('resolutionNumber')}
                                        placeholder="Введіть номер"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        required
                                        className="lviv-resolution-input"
                                    />
                                </div>
                                <small style={{ marginTop: 4, fontSize: 12, color: '#555', display: 'block' }}>
                                    Оберіть серію (<strong>ЛВ</strong> — повідомлення, <strong>РАП</strong> — постанова) та введіть номер.
                                </small>
                                <small style={{ marginTop: 2, fontSize: 12, color: '#111', fontWeight: 700, display: 'block' }}>
                                    Термін зарахування коштів до трьох робочих днів.
                                </small>
                            </div>

                            <div className="input-field">
                                <label htmlFor="carNumber">Номер авто</label>
                                <input
                                    type="text"
                                    id="carNumber"
                                    value={form.carNumber}
                                    onChange={handleChange('carNumber')}
                                    placeholder="Введіть державний номерний знак"
                                    required
                                />
                            </div>

                            <div className="input-field">
                                <label htmlFor="fullName">ПІБ платника</label>
                                <input
                                    type="text"
                                    id="fullName"
                                    value={form.fullName}
                                    onChange={handleChange('fullName')}
                                    placeholder="Введіть повне прізвище, імʼя та по батькові"
                                    required
                                />
                                <small style={{ marginTop: 4, fontSize: 12, color: '#777' }}>
                                    Наприклад: &quot;Іванов Іван Іванович&quot;
                                </small>
                            </div>

                            <div className="input-field">
                                <label htmlFor="email">Електронна пошта</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={form.email}
                                    onChange={handleChange('email')}
                                    placeholder="example@email.com"
                                    required
                                />
                            </div>

                            <div className="input-field">
                                <label htmlFor="payerInn">
                                    ІПН <span style={{ fontWeight: 400, color: '#777' }}>(опційно)</span>
                                </label>
                                <input
                                    type="text"
                                    id="payerInn"
                                    value={form.payerInn}
                                    onChange={handleChange('payerInn')}
                                    placeholder="10 цифр"
                                />
                            </div>

                            <div className="input-field">
                                <label htmlFor="amount">Сума</label>
                                <input
                                    type="number"
                                    id="amount"
                                    value={form.amount}
                                    onChange={handleChange('amount')}
                                    placeholder="170.00"
                                    min="0.01"
                                    step="0.01"
                                    required
                                />
                                <small style={{ marginTop: 4, fontSize: 12, color: '#777' }}>
                                    від 0.01 до 29&nbsp;999.99 грн за один переказ
                                </small>
                            </div>

                            {form.amount && (
                                <div className="lviv-summary" aria-live="polite">
                                    <div className="lviv-purpose">
                                        <p className="lviv-summary-title">Параметри платежу</p>
                                        <div className="lviv-summary-row">
                                            <span className="lviv-summary-label">Призначення платежу</span>
                                            <span className="lviv-purpose-code">
                                                {buildPaymentPurpose()}
                                            </span>
                                        </div>
                                        <div className="lviv-summary-row">
                                            <span className="lviv-summary-label">Отримувач</span>
                                            <span className="lviv-summary-value">
                                                ГУ ДКСУ У ЛЬВІВСЬКІЙ ОБЛАСТІ
                                            </span>
                                        </div>
                                        <div className="lviv-summary-row">
                                            <span className="lviv-summary-label">ЄДРПОУ</span>
                                            <span className="lviv-summary-value">38008294</span>
                                        </div>
                                        <div className="lviv-summary-row">
                                            <span className="lviv-summary-label">IBAN</span>
                                            <span className="lviv-summary-value">UA638999980314060542000013933</span>
                                        </div>
                                    </div>

                                    <p className="lviv-summary-title" style={{ marginTop: 12 }}>Підсумок оплати</p>

                                    <div className="lviv-summary-row">
                                        <span className="lviv-summary-label">Сума штрафу</span>
                                        <span className="lviv-summary-value">{formatUah(parseFloat(form.amount) || 0)} грн</span>
                                    </div>

                                    <div className="lviv-summary-row">
                                        <span className="lviv-summary-label">Комісія сервісу 15%</span>
                                        <span className="lviv-summary-value">{formatUah(serviceFee)} грн</span>
                                    </div>

                                    <div className="lviv-summary-total">
                                        <span className="lviv-summary-label">До сплати</span>
                                        <span className="lviv-summary-value">{formatUah(totalAmount)} грн</span>
                                    </div>

                                    <div className="lviv-summary-hint">
                                        Комісія додається до суми штрафу. Після натискання “Оплатити” ви будете перенаправлені на сторінку оплати.
                                    </div>
                                </div>
                            )}
                        </div>

                    {error && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {error}
                        </Typography>
                    )}

                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <button
                                type="submit"
                                className="gradient-button"
                                disabled={loading}
                            >
                                {loading ? 'Обробка...' : 'Оплатити'}
                            </button>
                            <Typography variant="body2" sx={{ mt: 1, color: '#777', fontSize: '14px' }}>
                                Натискаючи <strong>"Оплатити"</strong>, ви погоджуєтесь з умовами
                                <a href="/public_offer.pdf" target="_blank" style={{ color: '#007bff', textDecoration: 'none', marginLeft: 4 }}>
                                    Угоди користувача
                                </a>.
                            </Typography>
                        </Box>
                    </form>
                </div>
            </div>

            <footer className="footer">
                <div className="footer-content">
                    <div className="footer-left">
                        <h2 className="footer-brand">PayFine</h2>
                        <p className="footer-description">Офіційний сервіс сплати штрафів</p>
                        <p className="footer-agreement">
                            <a href="/public_offer.pdf" target="_blank" rel="noopener noreferrer">
                                Публічний договір
                            </a>
                        </p>
                    </div>

                    <div className="footer-center">
                        <p>
                            <FaMapMarkerAlt />
                            <a
                                href="https://maps.google.com/?q=м. Львів, вул. Водогінна, буд. 2А, офіс 62"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                м. Львів, вул. Водогінна, буд. 2А, офіс 62
                            </a>
                        </p>

                        <p>
                            <FaEnvelope />
                            <a href="mailto:support@payfine.com.ua">
                                support@payfine.com.ua
                            </a>
                        </p>
                    </div>

                    <div className="footer-right">
                        <div className="social-icons">
                            {/* резерв для соцмереж */}
                        </div>
                    </div>
                </div>
            </footer>
        </Container>
    );
}

export default Lviv;
